import { useState, useEffect, useMemo } from "react";
import type {
  WebMenu,
  MenuItem,
  Category,
  TagItem,
} from "@/types/webMenu";
import { productToMenuItem, webCatToCategory } from "@/types/webMenu";
import { useBrand } from "@/contexts/BrandContext";

export type { MenuItem, Category } from "@/types/webMenu";

export function usePublicMenu(branchId: string | null) {
  const { brand } = useBrand();
  const [webMenu, setWebMenu] = useState<WebMenu | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ code: string; message: string } | null>(
    null,
  );

  useEffect(() => {
    if (!branchId) {
      setWebMenu(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const fetchMenu = async () => {
      try {
        const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
        const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        const params = new URLSearchParams({ branch_id: branchId });
        if (brand.id) params.set("restaurant_id", brand.id);
        //const url = `https://${projectId}.supabase.co/functions/v1/web-menu?${params.toString()}`;

         const url = `${window.location.origin}/api/WebOrder/functions/v1/web-menu?${params.toString()}`;
        //const url = `https://saasmw.alkhwarizmi.xyz/api/WebOrder/functions/v1/web-menu?${params.toString()}`;


        const res = await fetch(url, {
          headers: {
            apikey: anonKey,
            Authorization: `Bearer ${anonKey}`,
          },
        });

        const json = await res.json();

        if (res.ok && json.success && json.data) {
          setWebMenu(json.data as WebMenu);
        } else {
          setError(
            json.error || { code: "UNKNOWN", message: "Failed to load menu" },
          );
          setWebMenu(null);
        }
      } catch (err: any) {
        setError({ code: "NETWORK_ERROR", message: err.message });
        setWebMenu(null);
      } finally {
        setLoading(false);
      }
    };

    fetchMenu();
  }, [branchId, brand.id]);

  const categories = useMemo<Category[]>(
    () => (webMenu?.categories || []).map(webCatToCategory),
    [webMenu],
  );

  
  const tagItems = useMemo<TagItem[]>(
    () => (webMenu?.tag_items || []).map((tag) => ({ ...tag })),
    [webMenu]
  );

  const menuItems = useMemo<MenuItem[]>(
    () =>
      (webMenu?.categories || []).flatMap((cat) =>
        (cat.products || []).map((p) => productToMenuItem(p, cat.id)),
      ),
    [webMenu],
  );

  return { webMenu, menuItems, categories,tagItems, loading, error };
}
